import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, detectRegion } from '../config/region';

// Vietnam FIRST (primary market) — was entirely missing before, which is why
// VN users on vn.honestlee.app had no local (+84) option. SEA + key tourist
// markets follow. Keep VN/TH/RU near the top (Đà Nẵng tourist mix).
const COUNTRY_CODES = [
  { code: '+84', label: 'VN +84', country: 'Vietnam' },
  { code: '+66', label: 'TH +66', country: 'Thailand' },
  { code: '+60', label: 'MY +60', country: 'Malaysia' },
  { code: '+65', label: 'SG +65', country: 'Singapore' },
  { code: '+855', label: 'KH +855', country: 'Cambodia' },
  { code: '+63', label: 'PH +63', country: 'Philippines' },
  { code: '+62', label: 'ID +62', country: 'Indonesia' },
  { code: '+971', label: 'AE +971', country: 'UAE' },
  { code: '+91', label: 'IN +91', country: 'India' },
  { code: '+7', label: 'RU +7', country: 'Russia' },
  { code: '+82', label: 'KR +82', country: 'South Korea' },
  { code: '+81', label: 'JP +81', country: 'Japan' },
  { code: '+86', label: 'CN +86', country: 'China' },
  { code: '+1', label: 'US +1', country: 'United States' },
  { code: '+44', label: 'UK +44', country: 'United Kingdom' },
  { code: '+61', label: 'AU +61', country: 'Australia' },
];

// Default the dial code to the current region's country (host-based).
const REGION_DIAL = { vn: '+84', th: '+66', ae: '+971', my: '+60', in: '+91' };

const ProfileCompletionScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [fullName, setFullName] = useState('');
  const [countryCode, setCountryCode] = useState(() => REGION_DIAL[detectRegion()] || '+84');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill display name from URL param or localStorage
  useEffect(() => {
    const nameParam = searchParams.get('name');
    const storedName = localStorage.getItem('user_name');
    if (nameParam) {
      setFullName(decodeURIComponent(nameParam));
    } else if (storedName) {
      setFullName(storedName);
    }

    const storedEmail = localStorage.getItem('user_email');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!fullName.trim()) {
      toast.error(t('auth.fullNameRequired'));
      return;
    }
    if (!phoneNumber.trim()) {
      toast.error(t('auth.phoneRequired'));
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('venueapp_token') || localStorage.getItem('venue_app_token') || localStorage.getItem('token');
      if (!token) {
        toast.error(t('auth.sessionExpiredRelogin'));
        navigate('/login', { replace: true });
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/auth/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: fullName.trim(),
          phone: `${countryCode}${phoneNumber.trim()}`,
          email: email.trim() || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.completeProfileFailed'));
      }

      // Mark profile as completed
      localStorage.setItem('profile_completed', 'true');
      if (fullName.trim()) localStorage.setItem('user_name', fullName.trim());
      if (email.trim()) localStorage.setItem('user_email', email.trim());

      toast.success(t('auth.profileCompletedSuccess'));

      // Check for pending invite token
      const pendingInviteToken = sessionStorage.getItem('pending_invite_token');
      if (pendingInviteToken) {
        // Accept the invite
        try {
          const inviteResponse = await fetch(`${apiBaseUrl}/auth/accept-invite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ token: pendingInviteToken })
          });

          const inviteData = await inviteResponse.json();
          sessionStorage.removeItem('pending_invite_token');

          if (inviteResponse.ok && inviteData.venueSlug) {
            navigate(`/venue-dashboard/${inviteData.venueSlug}`, { replace: true });
            return;
          }
        } catch (inviteErr) {
          console.error('[ProfileCompletion] Failed to accept invite:', inviteErr);
          sessionStorage.removeItem('pending_invite_token');
        }
      }

      // Default: redirect to map (home) — venue-dashboard only via invite flow
      const redirectTarget = sessionStorage.getItem('post_login_redirect') || '/';
      sessionStorage.removeItem('post_login_redirect');
      navigate(redirectTarget, { replace: true });

    } catch (err) {
      console.error('[ProfileCompletion] Error:', err);
      toast.error(err.message || t('auth.genericErrorRetry'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '16px'
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 448, width: '100%' }}
      >
        <div style={{
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '32px 32px 24px',
            background: 'linear-gradient(to right, #f59e0b, #ea580c)',
            color: 'white',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: 36,
              fontWeight: 'bold',
              marginBottom: 4,
              letterSpacing: '-0.5px'
            }}>
              HonestLee
            </div>
            <h2 style={{
              fontSize: 22,
              fontWeight: 'bold',
              margin: '12px 0 0'
            }}>
              {t('auth.completeProfileTitle')}
            </h2>
            <p style={{
              color: '#fef3c7',
              margin: '8px 0 0',
              fontSize: 14
            }}>
              {t('auth.completeProfileSubtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '24px 32px 32px' }}>
            {/* Full Name */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 6
              }}>
                {t('auth.fullNameLabel')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('auth.fullNamePlaceholder')}
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <p style={{
                fontSize: 12,
                color: '#9ca3af',
                margin: '4px 0 0',
                lineHeight: 1.4
              }}>
                {t('auth.fullNameHint')}
              </p>
            </div>

            {/* Phone Number */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 6
              }}>
                {t('auth.phoneNumberLabel')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  style={{
                    padding: '12px 8px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 14,
                    outline: 'none',
                    backgroundColor: '#f9fafb',
                    cursor: 'pointer',
                    minWidth: 95
                  }}
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={t('profile.phonePlaceholder')}
                  required
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>

            {/* Email (optional) */}
            <div style={{ marginBottom: 28 }}>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 6
              }}>
                {t('me.email')} <span style={{ color: '#9ca3af', fontWeight: 400 }}>{t('auth.optionalLabel')}</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <p style={{
                fontSize: 12,
                color: '#9ca3af',
                margin: '4px 0 0',
                lineHeight: 1.4
              }}>
                {t('auth.emailHint')}
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '14px 0',
                backgroundColor: submitting ? '#86efac' : '#06C755',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s, transform 0.1s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
              onMouseDown={(e) => { if (!submitting) e.target.style.transform = 'scale(0.98)'; }}
              onMouseUp={(e) => { e.target.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
            >
              {submitting ? (
                <>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {t('common.saving')}
                </>
              ) : (
                t('common.continue')
              )}
            </button>
          </form>
        </div>
      </motion.div>

      <style>
        {`@keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}
      </style>
    </div>
  );
};

export default ProfileCompletionScreen;
